import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { ShieldCheck, Cube, SignOut, GearSix, House, List, X } from "@phosphor-icons/react";
import { Button } from "./ui/button";

export default function Header() {
    const { user, logout } = useAuth();
    const loc = useLocation();
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    // Close the mobile menu on route change
    useEffect(() => { setMenuOpen(false); }, [loc.pathname]);

    const isActive = (to) => loc.pathname === to || (to !== "/" && loc.pathname.startsWith(to));

    const navItem = (to, label, testid) => (
        <Link
            to={to}
            data-testid={testid}
            className={`relative px-3 py-2 text-sm transition-colors ${
                isActive(to) ? "text-civic-700 font-semibold" : "text-slate-600 hover:text-slate-900"
            }`}
        >
            {label}
            {isActive(to) && (
                <span className="absolute -bottom-[13px] left-0 right-0 h-[2px] bg-civic-600" />
            )}
        </Link>
    );

    const mobileNavItem = (to, label, testid) => (
        <Link
            to={to}
            data-testid={testid}
            className={`block px-4 py-3 text-base border-b border-slate-100 transition-colors ${
                isActive(to) ? "text-civic-700 font-semibold bg-civic-50/60" : "text-slate-700 hover:bg-slate-50"
            }`}
        >
            {label}
        </Link>
    );

    return (
        <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur" data-testid="site-header">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-2">
                <Link to="/" className="flex items-center gap-2 sm:gap-2.5 min-w-0" data-testid="logo-link">
                    <img
                        src="/brand-logo.png"
                        alt="E-VoteVerify.com — Secure · Transparent · Verified · Powered by Blockchain"
                        className="h-10 sm:h-12 w-auto shrink-0"
                        loading="eager"
                        decoding="async"
                    />
                    <span className="sr-only">E-VoteVerify.com — Verified Ledger</span>
                </Link>

                <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
                    {navItem("/about", "About", "nav-home")}
                    {navItem("/features", "Features", "nav-features")}
                    {navItem("/learn-more", "Learn more", "nav-learn-more")}
                    {navItem("/explorer", "Explorer", "nav-explorer")}
                    {navItem("/results", "Results", "nav-results")}
                    {navItem("/audit", "Audit", "nav-audit")}
                </nav>

                <div className="flex items-center gap-1.5 sm:gap-2">
                    {user && user.role === "admin" && (
                        <Button variant="ghost" size="sm" onClick={() => nav("/admin")} data-testid="header-admin-btn" className="hidden sm:inline-flex">
                            <GearSix size={16} className="mr-1.5" /> Admin
                        </Button>
                    )}
                    {user && user.role === "voter" && (
                        <Button variant="ghost" size="sm" onClick={() => nav("/vote")} data-testid="header-vote-btn" className="hidden sm:inline-flex">
                            <House size={16} className="mr-1.5" /> Voter Hub
                        </Button>
                    )}
                    {user ? (
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => { await logout(); nav("/"); }}
                            data-testid="header-logout-btn"
                            className="border-slate-300 hidden sm:inline-flex"
                        >
                            <SignOut size={16} className="mr-1.5" />
                            Sign out
                        </Button>
                    ) : (
                        <>
                            <Link to="/login/voter" data-testid="header-voter-login-btn" className="hidden sm:inline-block">
                                <Button size="sm" variant="outline" className="border-slate-300">Voter Login</Button>
                            </Link>
                            <Link to="/login/admin" data-testid="header-admin-login-btn" className="hidden sm:inline-block">
                                <Button size="sm" className="bg-civic-600 hover:bg-civic-700 text-white">
                                    <Cube size={14} className="mr-1.5" weight="bold" />
                                    Admin
                                </Button>
                            </Link>
                        </>
                    )}

                    {/* Mobile hamburger */}
                    <button
                        type="button"
                        onClick={() => setMenuOpen((v) => !v)}
                        className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                        aria-label={menuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={menuOpen}
                        data-testid="header-mobile-toggle"
                    >
                        {menuOpen ? <X size={18} weight="bold" /> : <List size={18} weight="bold" />}
                    </button>
                </div>
            </div>

            {/* Mobile menu drawer */}
            {menuOpen && (
                <div className="md:hidden border-t border-slate-200 bg-white" data-testid="header-mobile-menu">
                    <div>
                        {mobileNavItem("/about", "About", "mnav-about")}
                        {mobileNavItem("/features", "Features", "mnav-features")}
                        {mobileNavItem("/learn-more", "Learn more", "mnav-learn-more")}
                        {mobileNavItem("/explorer", "Explorer", "mnav-explorer")}
                        {mobileNavItem("/results", "Results", "mnav-results")}
                        {mobileNavItem("/audit", "Audit", "mnav-audit")}
                    </div>
                    <div className="p-3 flex flex-col gap-2 border-t border-slate-100 bg-slate-50/60">
                        {user && user.role === "voter" && (
                            <Button variant="outline" size="sm" onClick={() => nav("/vote")} data-testid="mnav-voter-hub" className="w-full justify-center">
                                <House size={16} className="mr-1.5" /> Voter Hub
                            </Button>
                        )}
                        {user ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={async () => { await logout(); nav("/"); }}
                                className="w-full justify-center border-slate-300"
                                data-testid="mnav-logout"
                            >
                                <SignOut size={16} className="mr-1.5" /> Sign out
                            </Button>
                        ) : (
                            <Link to="/login/voter" data-testid="mnav-voter-login" className="block">
                                <Button size="sm" className="w-full bg-civic-600 hover:bg-civic-700 text-white">Voter Login</Button>
                            </Link>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
}
